import { useCallback } from 'react'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { IRouterLink } from 'core/plugins/model'
import Theme from 'core/themes/model'
import React, { MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import generateTestId from 'utils/test-helpers'
import Text, { TextVariant } from '../Text'
import Tooltip from '../tooltip'
import { createUrlWithQueryString } from 'core/plugins/route'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { KubernetesLogo } from 'k8s/logo'
// usePluginRouter removed - currentPluginId passed as prop to avoid re-renders
import { AppPlugins } from 'app/constants'
import { appStatus } from 'app/plugins/openstack/components/orchestration/app-catalog/helper'
import { topRight } from '../menu/defaults'
import Badge from '../badge'
import ItemHangerIcon from './ItemHangerIcon'

interface Props extends Omit<IRouterLink, 'nestedLinks'> {
  topLevel?: boolean
  isActive?: boolean
  tooltip?: string
  drawerOpen?: boolean
  compact?: boolean
  activeDisplayType?: 'background' | 'bar'
  className?: string
  tooltipProps?: { [key: string]: any }
  variant?: TextVariant
  onClick?: (event: MouseEvent) => void
  disabled?: boolean
  isBetaVersion?: boolean
  highlight?: boolean
  isLastChild?: boolean
  isLeaf?: boolean
  betaVersionTooltip?: string
  currentPluginId?: string
}

const LOGOS = { kubernetes: KubernetesLogo }

export default function NavItem({
  name,
  link,
  icon,
  onClick = undefined,
  className = undefined,
  drawerOpen = false,
  isActive = false,
  compact = false,
  tooltip = '',
  tooltipProps = {},
  activeDisplayType = 'background',
  variant = 'subtitle2',
  disabled = false,
  isBetaVersion = false,
  betaVersionTooltip = '',
  topLevel = false,
  highlight = false,
  isLastChild = false,
  isLeaf = false,
  currentPluginId = '',
}: Props) {
  const isHeader = !!icon
  // currentPluginId now passed as prop from parent
  const isAccountPlugin = currentPluginId === AppPlugins.MyAccount
  const theme = useTheme<Theme>()
  const classes = useStyles({
    isLastChild,
    highlight,
    isActive,
    compact,
    activeDisplayType,
    drawerOpen,
    disabled,
    isHeader,
    isAccountPlugin,
    isLeaf,
  })

  const handleClick = useCallback(
    (e) => {
      if (disabled) {
        return e.preventDefault()
      }

      onClick && onClick(e)
    },
    [onClick, disabled],
  )

  // Replace the link paramteres with values
  const { prefs: k8sGlobalPrefs } = useScopedPreferences('k8sPluginGlobalParams')
  const linkTo = link?.path?.includes(':')
    ? createUrlWithQueryString(link?.path || '', {
        cluster: k8sGlobalPrefs?.cluster as string,
      })
    : link?.path

  // Check if the icon is a logo
  const Logo = LOGOS[icon]
  const showLogo = Logo || icon

  return (
    <Link to={disabled ? undefined : linkTo} onClick={handleClick}>
      <li className={clsx(classes.navItem, className)} data-active={isActive ? 'true' : undefined}>
        {!topLevel && (
          <ItemHangerIcon
            className={classes.navItemHanger}
            fill={isActive ? theme.components.sidebar.activeIcon : theme.components.sidebar.border}
          />
        )}
        <Tooltip message={tooltip ? tooltip : ''} {...tooltipProps}>
          {showLogo && (
            <div className={clsx(classes.navIcon)}>
              {Logo ? (
                <Logo />
              ) : icon ? (
                <FontAwesomeIcon
                  data-testid={generateTestId(name)}
                  className="nav-icon"
                  title={name}
                  size="lg"
                >
                  {icon}
                </FontAwesomeIcon>
              ) : null}
            </div>
          )}
          {drawerOpen && (
            <div className={classes.badgeContainer}>
              <Text
                className={clsx('nav-text', classes.navText)}
                data-testid={generateTestId(name)}
                variant={compact ? 'sidenav2' : variant}
              >
                {name}
              </Text>
              {isBetaVersion && (
                <Tooltip className={classes.betaTagTooltip} message={betaVersionTooltip}>
                  <Badge className={classes.badge} variant={appStatus.deploying} text="Beta" />
                </Tooltip>
              )}
            </div>
          )}
        </Tooltip>
        {isLeaf && isActive ? <div className={`${classes.leafNotchMod} ${classes.notch}`} /> : null}
        {!drawerOpen && topLevel && isActive ? <div className={classes.notch} /> : null}
      </li>
    </Link>
  )
}

interface StyleProps {
  isLastChild: Props['isLastChild']
  highlight: Props['highlight']
  isActive: Props['isActive']
  compact: Props['compact']
  drawerOpen: Props['drawerOpen']
  activeDisplayType: Props['activeDisplayType']
  disabled: Props['disabled']
  isHeader: boolean
  isAccountPlugin: boolean
  isLeaf: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  notch: {
    background: theme.components.sidebar.activeIcon,
    width: '3px',
    height: '100%',
    marginLeft: 'auto',
    borderTopLeftRadius: theme.spacing(0.5),
    borderBottomLeftRadius: theme.spacing(0.5),
  },
  leafNotchMod: {
    height: '20px',
    marginTop: '9px',
  },
  navItemHanger: {
    zIndex: ({ isActive }) => (isActive ? 9 : 0),
  },
  navItem: {
    position: 'relative',
    cursor: ({ disabled }) => (disabled ? 'not-allowed' : ''),
    height: ({ compact, isLeaf }) => (compact || isLeaf ? 32 : 48),
    backgroundColor: ({ isActive, drawerOpen }) => {
      if (isActive && !drawerOpen) {
        return hexToRgbaCss(theme.components.sidebar.activeIcon, 0.1)
      }
      return 'transparent'
    },
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    '& .nav-text, & .nav-icon': {
      transition: 'color .2s ease',
    },
    '&:hover .nav-text, &:hover ': {
      color: ({ disabled }) =>
        disabled ? theme.palette.grey[500] : theme.components.sidebar.hoverText,
    },
    // '&:before': {
    //   content: '""',
    //   position: 'absolute',
    //   top: '50%',
    //   left: 0,
    //   width: '5px',
    //   height: '34px',
    //   transform: 'translate(0px, -50%)',
    //   backgroundColor: ({ isActive, isHeader }) =>
    //     isActive && !isHeader ? theme.components.sidebar.activeBackground : 'unset',
    //   borderRadius: '0 4px 4px 0',
    // },
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: 0,
      width: 1,
      height: '37%',
      zIndex: 9,
      backgroundColor: ({ isActive, highlight }) =>
        isActive || highlight
          ? theme.components.sidebar.activeIcon
          : theme.components.sidebar.border,
    },
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: 0,
      width: ({ isLastChild }) => (isLastChild ? 0 : 1),
      height: '102%',
      // backgroundColor: theme.components.sidebar.border,
      backgroundColor: ({ highlight }) =>
        highlight ? theme.components.sidebar.activeIcon : theme.components.sidebar.border,
    },
  },
  navIcon: {
    borderRadius: theme.spacing(0.5),
    backgroundColor: ({ isActive, drawerOpen }) =>
      isActive && drawerOpen ? theme.components.sidebar.activeIcon : 'transparent',
    minWidth: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: ({ isActive }) =>
      isActive ? theme.components.sidebar.activeText : theme.palette.grey[700],
    '& > svg': {
      width: ({ isActive, drawerOpen }) =>
        isActive && drawerOpen ? '18px !important' : '20px !important',
      height: ({ isActive, drawerOpen }) =>
        isActive && drawerOpen ? '18px !important' : '20px !important',
    },
    '& > svg path': {
      fill: ({ isActive, drawerOpen }) => (isActive && drawerOpen ? 'white' : ''),
    },
    '& > i': {
      width: ({ isActive, drawerOpen }) => (isActive && drawerOpen ? '18px' : '20px'),
      height: ({ isActive, drawerOpen }) => (isActive && drawerOpen ? '18px' : '20px'),
      fontSize: ({ isActive, drawerOpen }) => (isActive && drawerOpen ? '18px' : '20px'),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: ({ isActive, drawerOpen }) =>
        isActive && drawerOpen ? theme.palette.grey['000'] : theme.components.sidebar.text,
      // '&:before': {
      //   fontSize: ({ isActive, drawerOpen }) => (isActive && drawerOpen ? '18px' : '20px'),
      // },
    },
  },
  navText: {
    color: ({ isActive, isLeaf }) => {
      // For subsections (leaf items)
      if (isLeaf) {
        return isActive ? theme.palette.grey[700] : theme.palette.grey[500]
      }
      // For top-level sections
      return isActive ? theme.components.sidebar.activeText : theme.palette.grey[700]
    },
  },
  badgeContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  betaTagTooltip: {
    padding: 0,
    '& .tooltip-container': {
      padding: 0,
    },
  },
}))
